import React, { useState } from 'react';
import { Keypair } from '@solana/web3.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { AlertTriangle, Eye, EyeOff, Shield } from 'lucide-react';
import { Input } from './ui/input';


export function PrivateKeyDialog({
  open,
  onOpenChange,
  onConfirm,
  title = "Enter Private Key",
  description = "Please enter your mint authority private key to proceed with token distribution."
}) {
  const [privateKeyInput, setPrivateKeyInput] = useState('');
  const [inputFormat, setInputFormat] = useState('array');
  const [showKey, setShowKey] = useState(false);
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const validateAndConfirm = async () => {
    if (!privateKeyInput.trim()) {
      setError('Private key is required');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      let keypair;

      if (inputFormat === 'array') {
        // Parse as JSON array format
        const secretKeyArray = JSON.parse(privateKeyInput.trim());
        
        if (!Array.isArray(secretKeyArray) || secretKeyArray.length !== 64) {
          throw new Error('Invalid private key format. Expected array of 64 numbers.');
        }

        keypair = Keypair.fromSecretKey(new Uint8Array(secretKeyArray));
      } else {
        // Parse as base58 format
        const bs58 = await import('bs58');
        const secretKey = bs58.default.decode(privateKeyInput.trim());
        keypair = Keypair.fromSecretKey(secretKey);
      }

      // Clear the input immediately for security
      setPrivateKeyInput('');
      
      // Confirm with the keypair
      onConfirm(keypair);
      onOpenChange(false);
    } catch (err) {
      console.error('Private key validation error:', err);
      setError(
        err instanceof Error 
          ? err.message 
          : 'Invalid private key format. Please check your input.'
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleCancel = () => {
    setPrivateKeyInput('');
    setError('');
    onOpenChange(false);
  };

  const handleFormatChange = (format) => {
    setInputFormat(format);
    setPrivateKeyInput('');
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-orange-500" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your private key is processed locally and never stored or transmitted. 
              It will be cleared from memory immediately after use.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="format-select">Private Key Format</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={inputFormat === 'array' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFormatChange('array')}
              >
                JSON Array
              </Button>
              <Button
                type="button"
                variant={inputFormat === 'base58' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFormatChange('base58')}
              >
                Base58
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="private-key">
                Private Key {inputFormat === 'array' ? '(JSON Array)' : '(Base58)'}
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            
            {inputFormat === 'array' ? (
              <Textarea
                id="private-key"
                placeholder="[123,45,67,89,12,34,56,78,90,12,34,56,78,90,12,34,56,78,90,12,34,56,78,90,12,34,56,78,90,12,34,56,...]"
                value={showKey ? privateKeyInput : privateKeyInput.replace(/./g, '•')}
                onChange={(e) => {
                  // Handle the masked input
                  if (!showKey) {
                    // This is a simplified approach - in production you'd want more sophisticated handling
                    const realValue = e.target.value.replace(/•/g, '');
                    setPrivateKeyInput(realValue);
                  } else {
                    setPrivateKeyInput(e.target.value);
                  }
                }}
                className="min-h-[100px] font-mono text-sm"
              />
            ) : (
              <Input
                id="private-key"
                placeholder="5J1F7GHaDxuFAQjzZ2VtfF4F4F4F4F4F4F4F4F4F4F4F4F4F4F4F4F..."
                value={privateKeyInput}
                onChange={(e) => setPrivateKeyInput(e.target.value)}
                type={showKey ? 'text' : 'password'}
                className="font-mono text-sm"
              />
            )}
          </div>

          {inputFormat === 'array' && (
            <div className="text-xs text-muted-foreground">
              <p><strong>Format:</strong> JSON array of 64 numbers</p>
              <p><strong>Example:</strong> [123,45,67,89,12,34,56,78,90,12,34,56,78,90,12,34,56,78,90,12,34,56,78,90,12,34,56,78,90,12,34,56,...]</p>
            </div>
          )}

          {inputFormat === 'base58' && (
            <div className="text-xs text-muted-foreground">
              <p><strong>Format:</strong> Base58 encoded string</p>
              <p><strong>Example:</strong> 5J1F7GHaDxuFAQjzZ2VtfF4F4F4F4F4F4F4F4F4F4F4F4F4F4F4F4F...</p>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={validateAndConfirm} 
            disabled={!privateKeyInput.trim() || isValidating}
          >
            {isValidating ? 'Validating...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
